import { Link } from 'react-router-dom';
import { Card, Notice } from '../components/ui';

/**
 * v2 §24–28.
 *
 * These modules are reachable in the nav and explain themselves rather than
 * being silently absent. Whoever opens this console asked for membership and
 * profit sharing; they should be able to see that it is accounted for, and
 * exactly what is standing in the way.
 */
export function BlockedPage({ module }: { module: 'membership' | 'profit-sharing' }) {
  const isMembership = module === 'membership';

  return (
    <div className="stack" style={{ maxWidth: 780 }}>
      <Notice tone="danger" title="Not built — waiting on a legal opinion">
        {isMembership
          ? 'Membership tiers (IUC / SBUC / TUC), the paywall on first open, membership cards and tier-targeted notifications are specified in v2 §24–28 and are deliberately not implemented.'
          : 'Distributing 30% of company net profit to members is specified in v2 §28 and is deliberately not implemented.'}
      </Notice>

      {/* The calculation half of §28 now exists under Settings. Saying so here
          rather than rewriting this page: what is on hold is the membership
          scheme, and that has not changed. */}
      {!isMembership && (
        <Notice tone="info" title="The calculation exists, the scheme does not">
          Profit can be calculated, allocated and revealed from{' '}
          <Link to="/settings/profit">Settings → Profit distribution</Link>, over eligibility rules that read real
          accounts. The IUC / SBUC / TUC membership tiers it was specified against are still not built, and no money
          leaves the company either way.
        </Notice>
      )}

      <Card>
        <h3 style={{ margin: '0 0 10px', fontSize: 16 }}>Why it is on hold</h3>
        <p style={{ margin: 0, lineHeight: 1.65, fontSize: 13.6, color: 'var(--ink)' }}>
          As specified, the scheme takes ₹500–₹1,000 from the public, caps membership at fixed numbers, gates app access
          behind that payment, promises <strong>30% of company net profit</strong> back to those members, and pays
          bonuses for enrolling more paying members.
        </p>
        <p style={{ lineHeight: 1.65, fontSize: 13.6 }}>
          That combination — pooled public money, capped units, returns promised from company profit — is the definition
          of a <strong>Collective Investment Scheme</strong> under the SEBI (Collective Investment Schemes) Regulations,
          1999. Running one without SEBI registration is an offence under the SEBI Act, with personal liability for
          promoters. It plausibly also engages the BUDS Act 2019 and the Prize Chits and Money Circulation Schemes
          (Banning) Act 1978.
        </p>
        <p style={{ lineHeight: 1.65, fontSize: 13.6, marginBottom: 0 }}>
          Building the tables would be building the scheme, so there is nothing here to switch on by accident. Everything
          else in the product is unaffected and shipped.
        </p>
      </Card>

      <Card>
        <h3 style={{ margin: '0 0 10px', fontSize: 16 }}>What unblocks it</h3>
        <ol style={{ margin: 0, paddingLeft: 20, lineHeight: 1.85, fontSize: 13.6 }}>
          <li>
            A written opinion from a securities lawyer on the scheme <em>as it would actually operate</em> — the prices,
            the caps, the profit share, and the enrolment bonuses together.
          </li>
          <li>
            A decision on <strong>OQ-V2-2</strong>: membership through Apple and Google in-app purchase at 15–30%
            commission, or restructured so it is not a precondition for using the app. A hard paywall on first open is
            the highest-risk framing for both stores.
          </li>
          <li>
            <strong>OQ-V2-4</strong>: SBUC and TUC prices, which were never set.
          </li>
          <li>
            <strong>OQ-V2-1</strong>: whether the paywall overrides v1's rule that consumers use the app free.
          </li>
        </ol>
      </Card>

      <Card>
        <h3 style={{ margin: '0 0 10px', fontSize: 16 }}>What already works without it</h3>
        <p style={{ margin: 0, lineHeight: 1.65, fontSize: 13.6 }}>
          Listing slots, boosts, provider subscriptions and partner subscriptions are all built and chargeable — they
          sell access to real-world goods and services, which is why the app stores exempt them from in-app purchase
          and why they raise none of the questions above.
        </p>
      </Card>
    </div>
  );
}
